/**
 * Cache key builder utilities.
 * Generates consistent, deterministic cache keys for API responses.
 */

/**
 * Sorts query params deterministically and produces a stable cache key.
 */
const buildParamsKey = (params: Record<string, string | number | string[] | undefined | null>): string => {
  const entries = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${Array.isArray(v) ? v.sort().join(',') : v}`);
  return entries.length > 0 ? entries.join('&') : '';
};

export const cacheKeys = {
  albums: (params: Record<string, string | number | string[] | undefined | null> = {}): string => {
    const suffix = buildParamsKey(params);
    return suffix ? `albums:${suffix}` : 'albums';
  },

  userAlbums: (userId: string, params: Record<string, string | number | string[] | undefined | null> = {}): string => {
    const suffix = buildParamsKey(params);
    return suffix ? `user-albums:${userId}:${suffix}` : `user-albums:${userId}`;
  },

  album: (albumId: string): string => `album:${albumId}`,

  albumPhotos: (albumId: string): string => `album-photos:${albumId}`,

  photos: (params: Record<string, string | number | string[] | undefined | null> = {}): string => {
    const suffix = buildParamsKey(params);
    return suffix ? `photos:${suffix}` : 'photos';
  },

  userProfile: (): string => 'user:me',

  userById: (userId: string): string => `user:${userId}`,

  friends: (params: Record<string, string | number | string[] | undefined | null> = {}): string => {
    const suffix = buildParamsKey(params);
    return suffix ? `friends:${suffix}` : 'friends';
  },

  albumReactions: (albumId: string): string => `reactions:album:${albumId}`,

  photoReactions: (albumId: string, photoId: string): string => `reactions:photo:${albumId}:${photoId}`,

  albumComments: (albumId: string): string => `comments:album:${albumId}`,

  photoComments: (albumId: string, photoId: string): string => `comments:photo:${albumId}:${photoId}`,

  socialEvents: (params: Record<string, string | number | string[] | undefined | null> = {}): string => {
    const suffix = buildParamsKey(params);
    return suffix ? `social-events:${suffix}` : 'social-events';
  },

  friendGroups: (): string => 'friend-groups',

  friendGroup: (groupId: string): string => `friend-group:${groupId}`,

  geoPhotos: (params: Record<string, string | number | string[] | undefined | null> = {}): string => {
    const suffix = buildParamsKey(params);
    return suffix ? `geo-photos:${suffix}` : 'geo-photos';
  },
};

/**
 * Checks if a cache key matches a given prefix.
 */
export const matchesCachePrefix = (key: string, prefix: string): boolean => {
  return key === prefix || key.startsWith(`${prefix}:`);
};

/**
 * Per-prefix TTL (max age) for cached entries. After this age the entry is
 * dropped from IndexedDB on read (returns null, forcing a fresh fetch). The
 * `useCachedQuery` hook always revalidates against the server using ETags, so
 * TTL only governs how long we keep an entry around as a fallback / instant
 * paint source.
 */
const DEFAULT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const TTL_BY_PREFIX: Record<string, number> = {
  // Highly volatile — short TTL to avoid showing very stale data on cold start
  'social-events': 5 * 60 * 1000,        // 5 min
  'reactions': 10 * 60 * 1000,           // 10 min
  'comments': 10 * 60 * 1000,            // 10 min
  // Mid-volatile
  'friends': 60 * 60 * 1000,             // 1 hour
  'friend-groups': 60 * 60 * 1000,
  'friend-group': 60 * 60 * 1000,
  'geo-photos': 30 * 60 * 1000,          // 30 min
  'user': 24 * 60 * 60 * 1000,           // 1 day
  // Album metadata — short TTL because embedded SAS tokens (thumbnailUrl /
  // mediumUrl) only live ~5 minutes server-side. ETag-based revalidation still
  // refreshes URLs on every load; this TTL bounds the worst-case window in
  // which a long-untouched cache could surface expired URLs.
  'albums': 60 * 60 * 1000,             // 1 hour
  'user-albums': 60 * 60 * 1000,        // 1 hour
  'album': 60 * 60 * 1000,              // 1 hour
  'album-photos': 60 * 60 * 1000,       // 1 hour
  'photos': 60 * 60 * 1000,             // 1 hour
};

/**
 * Returns the configured TTL (in ms) for a given cache key, based on its prefix.
 */
export const getCacheTTL = (key: string): number => {
  const prefix = key.split(':')[0];
  return TTL_BY_PREFIX[prefix] ?? DEFAULT_TTL_MS;
};
